import toast from "react-hot-toast";
import {
  AnimationState,
  Category,
  Guess,
  Puzzle,
  State,
} from "../../models/gameModels";
import { UserData } from "../../services/firebase";
import {
  STATE_KEY,
  cacheState,
  clearUserState,
  stateFromPuzzle,
} from "../../services/state";
import { shuffle } from "../../utils/list-utils";
import { GameEvents } from "./Game";
import { clearTimer } from "../../services/timer";

export const gameMethods = (state: State & { triggers: GameEvents }) => {
  return {
    setPuzzle(puzzle: Puzzle) {
      Object.assign(state, {
        ...stateFromPuzzle(puzzle),
        triggers: state.triggers,
      });
      cacheState(state);
    },
    endAnimation(finalState: AnimationState) {
      state.animationInProgress = finalState;
    },
    toggleActive(item: string) {
      if (state.activeItems.includes(item)) {
        state.activeItems = state.activeItems.filter((i) => i !== item);
      } else if (state.activeItems.length < 4) {
        state.activeItems.push(item);
      }
    },
    shuffle() {
      shuffle(state.items);
    },
    deselectAll() {
      state.activeItems = [];
      return;
    },
    clear(auth: UserData | undefined) {
      localStorage.removeItem(STATE_KEY);
      clearTimer();
      clearUserState(auth).then(() => window.location.reload());
      return;
    },
    submit() {
      const words = [...state.activeItems].sort();
      if (words.length !== 4) {
        toast("אנא בחרו 4 מילים", {
          duration: 3000,
          position: "bottom-center",

          // Styling
          style: {},
          className: "",

          // Change colors of success/error/loading icon
          iconTheme: {
            primary: "#fff",
            secondary: "#000",
          },

          // Aria
          ariaProps: {
            role: "status",
            "aria-live": "polite",
          },
        });
        return;
      }
      const guessed = state.guesses.find((guess) =>
        guess.words.every((guess) => words.includes(guess.word)),
      );

      const foundGroup = state.incomplete.find((group) =>
        group.words.every((item) => state.activeItems.includes(item)),
      );

      const oneAway = foundGroup
        ? undefined
        : state.incomplete.find(
            (group) =>
              group.words.filter((item) => state.activeItems.includes(item))
                .length === 3,
          );

      if (oneAway && !guessed) {
        toast("כמעט!", {
          duration: 3000,
          position: "bottom-center",

          // Styling
          style: {},
          className: "",

          // Custom Icon
          icon: "🤏",

          // Change colors of success/error/loading icon
          iconTheme: {
            primary: "#fff",
            secondary: "#000",
          },

          // Aria
          ariaProps: {
            role: "status",
            "aria-live": "polite",
          },
        });
      }

      const guess: Guess = {
        category: foundGroup?.name ?? null,
        one_way_category: oneAway?.name ?? null,
        correct: !!foundGroup,
        timestamp: new Date(),
        words: state.activeItems
          .map((word) => ({
            word,
            category: state.initialPuzzle.categories.find((c) =>
              c.words.includes(word),
            )?.name!!,
            difficulty: state.initialPuzzle.categories.find((c) =>
              c.words.includes(word),
            )?.difficulty!!,
          }))
          .sort((a, b) => a.word.localeCompare(b.word)),
      };

      if (guessed) {
        toast("כבר ניחשת את זה", {
          duration: 3000,
          position: "bottom-center",

          // Styling
          style: {},
          className: "",

          // Change colors of success/error/loading icon
          iconTheme: {
            primary: "#fff",
            secondary: "#000",
          },

          icon: oneAway && "🤏",

          // Aria
          ariaProps: {
            role: "status",
            "aria-live": "polite",
          },
        });
        state.activeItems = [];
        return;
      }
      state.triggers.onGuess(state, guess);
      state.guesses.push(guess);

      state.animationInProgress = "GUESS-STARTED";
      if (foundGroup) {
        state.foundGroup = foundGroup;
      }
    },
    lose() {
      state.animationInProgress = "LOSE-ANIMATION";
      state.result = "lose";
      state.triggers.onLose(state);
    },
    win() {
      state.animationInProgress = "WIN";
      state.result = "win";
      state.triggers.onWin(state);
    },
    changeState(newState: State) {
      Object.assign(state, { ...newState, triggers: state.triggers });
      cacheState(state);
      return;
    },
    solveOne() {
      let solution = state.incomplete.shift() as Category;
      state.complete.push(solution);
      const itemsToMove = state.items.filter(
        (item, index) => !solution?.words.includes(item) && index < 4,
      );
      const emptySpots = state.items
        .map((item, index) => ({ item, index }))
        .filter(
          ({ item, index }) => solution.words.includes(item) && index >= 4,
        )
        .map(({ index }) => index);
      const items = state.items.slice(4);
      itemsToMove.forEach((item, idx) => (items[emptySpots[idx] - 4] = item));
      state.items = items;
      state.animationInProgress = "LOSE-ANIMATION";
      cacheState(state);
    },
    finishSubmit() {
      if (state.foundGroup) {
        state.complete.push(state.foundGroup);
        const itemsToMove = state.items.filter(
          (item, index) => !state.foundGroup?.words.includes(item) && index < 4,
        );
        const emptySpots = state.items
          .map((item, index) => ({ item, index }))
          .filter(
            ({ item, index }) =>
              state.foundGroup?.words.includes(item) && index >= 4,
          )
          .map(({ index }) => index);
        state.incomplete = state.incomplete.filter(
          (group) =>
            group.words.join(",") !== state.foundGroup?.words.join(","),
        );
        const items = state.items.slice(4);
        itemsToMove.forEach((item, idx) => (items[emptySpots[idx] - 4] = item));
        state.items = items;
        state.activeItems = [];
        if (state.incomplete.length === 0 && state.foundGroup) {
          this.win();
          state.animationInProgress = "WIN";
          cacheState(state);
          return;
        } else {
          state.triggers.onGuess(state, undefined);
        }
      } else {
        state.mistakesRemaining -= 1;
        state.activeItems = [];

        if (state.mistakesRemaining === 0) {
          this.lose();
          return;
        } else {
          state.animationInProgress = "NONE";
          state.triggers.onGuess(state, undefined);
        }
      }
      state.foundGroup = undefined;
      state.animationInProgress = "NONE";
      return;
    },
  };
};

export type GameMethods = ReturnType<typeof gameMethods>;
